/**
 * Audit Clauses Data Hook
 *
 * Fetches standard clause data for an audit from the backend
 * and returns it as a StandardClauseSet.
 * Filters clauses to show only those matching the current locale,
 * falling back to all clauses if no locale match exists.
 */

import { useMemo } from "react";
import { api } from "convex/_generated/api";
import type {
  AuditId,
  StandardClauseSet,
  StandardSection,
} from "@/packages/types";
import { useWorkspaceQuery } from "@/hooks";
import { getCurrentLanguage } from "@/packages/utils";

/* ------------------------------------------------------------------ */
/* useAuditClauses                                                     */
/* ------------------------------------------------------------------ */

interface UseAuditClausesReturn {
  clauseSet: StandardClauseSet | null;
  isLoading: boolean;
}

function filterSectionsByLocale(
  sections: StandardSection[],
  locale: string,
): StandardSection[] {
  const filtered = sections
    .map((section) => ({
      ...section,
      clauses: section.clauses.filter(
        (c) => !c.language || c.language === locale,
      ),
    }))
    .filter((section) => section.clauses.length > 0);

  // Fall back to all clauses if no locale-matching clauses exist
  if (filtered.length === 0) return sections;
  return filtered;
}

export function useAuditClauses(
  auditId: AuditId | null,
): UseAuditClausesReturn {
  const { data, isLoading } = useWorkspaceQuery(
    api.audit_queries.getStandardClausesForAudit,
    { auditId: auditId ?? null },
  );

  const clauseSet = useMemo(() => {
    const raw = (data as StandardClauseSet | undefined) ?? null;
    if (!raw) return null;

    const locale = getCurrentLanguage();
    return {
      ...raw,
      sections: filterSectionsByLocale(raw.sections, locale),
    };
  }, [data]);

  return {
    clauseSet,
    isLoading,
  };
}
